import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import StartIcon from 'components/ReusableComponents/Icons/Start'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import { useInternalAdmin } from 'hooks'
import { AnalyticsContextWrapper } from 'hooks/useAllHotels'
import useClickOut from 'hooks/useClickOut'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './MontlyRevenue.module.scss'
import RevenueTooltip from './RevenueTooltip'

const YearPicker = ({ firstYear, secondYear, minimumAvailableYear, setYears }) => {
  const [selectedFirstYear, setSelectedFirstYear] = useState()
  const [selectedSecondYear, setSelectedSecondYear] = useState()

  const [isFirstYearPickerVisible, setisFirstYearPickerVisible] = useState(false)
  const [isSecondYearPickerVisible, setisSecondYearPickerVisible] = useState(false)

  function handleClickPicker() {
    if (!isFirstYearPickerVisible && !isSecondYearPickerVisible) {
      setisFirstYearPickerVisible(true)
    }
  }

  function handlePickFirstYear(year) {
    setSelectedFirstYear(year)
    setisFirstYearPickerVisible(false)
    setisSecondYearPickerVisible(true)
  }

  function handlePickSecondYear(secondYear) {
    setSelectedSecondYear(secondYear)
    setisSecondYearPickerVisible(false)
    setYears(selectedFirstYear, secondYear)
  }

  const firstYearPickerRef = useRef()

  useClickOut(firstYearPickerRef, () => {
    if (isFirstYearPickerVisible) {
      setisFirstYearPickerVisible(false)
      setSelectedFirstYear()
      setSelectedSecondYear()
    }
  })

  const secondYearPickerRef = useRef()

  useClickOut(secondYearPickerRef, () => {
    if (isSecondYearPickerVisible) {
      setisSecondYearPickerVisible(false)
      setSelectedFirstYear()
      setSelectedSecondYear()
    }
  })

  return (
    <div className={styles.yearVersusPicker}>
      <div onClick={handleClickPicker}>
        {!selectedFirstYear && !selectedSecondYear && (
          <>
            {' '}
            {firstYear} <span className={styles.versus}>vs</span>
            {secondYear}
          </>
        )}
        {selectedFirstYear && !selectedSecondYear && (
          <>
            {' '}
            {selectedFirstYear} <span className={styles.versus}>vs</span>-
          </>
        )}
        {selectedFirstYear && selectedSecondYear && (
          <>
            {selectedFirstYear} <span className={styles.versus}>vs</span>
            {selectedSecondYear}
          </>
        )}
      </div>
      {isFirstYearPickerVisible && (
        <div className={styles.yearPicker} ref={firstYearPickerRef}>
          <p className={styles.yearPickerLabel}>Select first year</p>
          {Array(Number(moment().format('YYYY')) - minimumAvailableYear)
            .fill(Number(moment().format('YYYY')))
            .map((year, index) => year - index - 1)
            .map(year => (
              <button
                key={year}
                className={styles.yearPickerButton}
                onClick={() => handlePickFirstYear(year)}
              >
                {year}
              </button>
            ))}
        </div>
      )}
      {isSecondYearPickerVisible && (
        <div className={styles.yearPicker} ref={secondYearPickerRef}>
          <p className={styles.yearPickerLabel}>Select second year</p>
          {Array(Number(moment().format('YYYY')) - selectedFirstYear)
            .fill(Number(moment().format('YYYY')))
            .map((year, index) => year - index)
            .map(year => (
              <button
                key={year}
                className={styles.yearPickerButton}
                onClick={() => handlePickSecondYear(year)}
              >
                {year}
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

const RadioGroup = ({ value, onChange, options, label }) => {
  return (
    <div>
      <p className={styles.radioGroupLabel}>{label}</p>
      <div className={styles.radioGroup}>
        {Object.keys(options).map(key => (
          <label key={key} className={styles.radioGroupItem}>
            <input
              type="radio"
              name="kind"
              value={key}
              checked={value === key}
              onChange={() => onChange(key)}
            />
            <span>{options[key]}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function thousandAndMillionFormatter(value) {
  if (value === 0) {
    return value
  }

  if (value % 1000000 === 0) {
    return `${value / 1000000}M`
  }

  if (value % 1000 === 0) {
    return `${value / 1000}K`
  }

  return value
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0
})

const barColors = {
  revenue: {
    firstYear: '#CBD4E0',
    secondYear: '#4892EE'
  },
  bookings: {
    firstYear: '#DDD8C9',
    secondYear: '#EAB20F'
  },
  messages: {
    firstYear: '#D7DADC',
    secondYear: '#2B6CB0'
  }
}

const barSize = 10

const SingleChart = ({
  label,
  firstYear,
  secondYear,
  firstYearTotal,
  secondYearTotal,
  deltaPercentage,
  deltaFlat,
  syncId,
  data,
  firstYearColor,
  secondYearColor,
  serviceStartDate,
  valueFormatter = value => value,
  isHighDeltaGood = true
}) => {
  return (
    <div className={styles.chartBox}>
      <Summary
        firstYear={firstYear}
        secondYear={secondYear}
        firstYearColor={firstYearColor}
        secondYearColor={secondYearColor}
        label={label}
        firstYearValue={firstYearTotal}
        secondYearValue={secondYearTotal}
        deltaPercentage={deltaPercentage}
        deltaFlat={deltaFlat}
        isHighDeltaGood={isHighDeltaGood}
      />
      <div className={styles.chartBoxInner}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={500}
            height={300}
            data={data}
            syncId={syncId}
            margin={{
              top: 0,
              right: 40,
              left: 20,
              bottom: 15
            }}
          >
            <CartesianGrid stroke="#F2F2F2" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
              tick={{
                fill: '#7D7D7D',
                fontSize: 12,
                transform: 'translate(0, 14)'
              }}
              tickSize={0}
              padding={{ left: 40, right: 40 }}
            />
            <YAxis
              tickSize={0}
              axisLine={false}
              allowDecimals={false}
              tick={{
                fill: '#7D7D7D',
                fontSize: 12
              }}
              padding={{ top: 35, bottom: 0 }}
              tickFormatter={thousandAndMillionFormatter}
              range
            />
            <Tooltip
              content={
                <RevenueTooltip
                  labelFormatter={date => date}
                  dataTitle={label}
                  firstYear={firstYear}
                  secondYear={secondYear}
                  valueFormatter={valueFormatter}
                />
              }
              cursor={{
                stroke: 'red',
                strokeWidth: 0,
                fill: '#dedede',
                opacity: 0.22
              }}
            />
            {[secondYear.toString()].includes(
              moment(serviceStartDate).format('YYYY')
            ) && (
              <ReferenceLine
                x={moment(serviceStartDate).format('MMM')}
                style={{
                  transform: `translateX(${'7px'})`
                }}
                strokeDasharray="3 3"
                isFront={false}
                label={props => (
                  <LaunchTooltip
                    {...props}
                    serviceStartDate={serviceStartDate}
                    style={{
                      transform: `translateX('7px'})`
                    }}
                    isFirstYear={
                      firstYear.toString() === moment(serviceStartDate).format('YYYY')
                    }
                  />
                )}
                stroke="#2158CF"
              />
            )}
            <Bar dataKey="firstYear" fill={firstYearColor} barSize={barSize} />
            <Bar dataKey="secondYear" fill={secondYearColor} barSize={barSize} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

const Charts = ({ data, firstYear, secondYear, name, syncId, dataToShow }) => {
  return (
    <div className={styles.chartsHolder}>
      <div className={styles.chartsHeader}>
        {name && <p className={styles.groupBreakdownTitle}>{name}</p>}
        {data.serviceStartDate && (
          <p className={styles.serviceStartDate}>
            <StartIcon />
            {moment(data.serviceStartDate).format('MMMM D, YYYY')}
          </p>
        )}
      </div>

      <SingleChart
        label="revenue"
        data={data.monthlyRevenues}
        firstYear={firstYear}
        secondYear={secondYear}
        syncId={syncId}
        firstYearColor={barColors.revenue.firstYear}
        secondYearColor={barColors.revenue.secondYear}
        deltaPercentage={data.revenueDeltaPercentage}
        deltaFlat={data.revenueDeltaFlat}
        firstYearTotal={currencyFormatter.format(data.firstYearRevenue)}
        secondYearTotal={currencyFormatter.format(data.secondYearRevenue)}
        serviceStartDate={data.serviceStartDate}
        valueFormatter={value => currencyFormatter.format(value)}
        isHighDeltaGood={[dataTypesToShow.Direct, dataTypesToShow.Consumed].includes(
          dataToShow
        )}
      />
      <SingleChart
        label="bookings"
        data={data.monthlyBookings}
        firstYear={firstYear}
        secondYear={secondYear}
        syncId={syncId}
        firstYearColor={barColors.bookings.firstYear}
        secondYearColor={barColors.bookings.secondYear}
        deltaPercentage={data.bookingsDeltaPercentage}
        deltaFlat={data.bookingsDeltaFlat}
        firstYearTotal={formatNumberAsThousands(data.firstYearBookings)}
        secondYearTotal={formatNumberAsThousands(data.secondYearBookings)}
        serviceStartDate={data.serviceStartDate}
        isHighDeltaGood={[dataTypesToShow.Direct, dataTypesToShow.Consumed].includes(
          dataToShow
        )}
      />
      <SingleChart
        label="messages"
        data={data.monthlyMessages}
        firstYear={firstYear}
        secondYear={secondYear}
        syncId={syncId}
        firstYearColor={barColors.messages.firstYear}
        secondYearColor={barColors.messages.secondYear}
        deltaPercentage={data.messagesDeltaPercentage}
        deltaFlat={data.messagesDeltaFlat}
        firstYearTotal={formatNumberAsThousands(data.firstYearMessages)}
        secondYearTotal={formatNumberAsThousands(data.secondYearMessages)}
        serviceStartDate={data.serviceStartDate}
        isHighDeltaGood={[dataTypesToShow.Direct, dataTypesToShow.Consumed].includes(
          dataToShow
        )}
      />
    </div>
  )
}

const Summary = ({
  firstYear = 2020,
  secondYear = 2021,
  label = 'revenue',
  firstYearValue = '$370,252',
  secondYearValue = '$370,252',
  deltaPercentage = 8.2,
  isHighDeltaGood = true,
  firstYearColor,
  secondYearColor,
  name
}) => (
  <div className={styles.summaryHolder}>
    {name && (
      <div className={styles.summaryNameHolder}>
        <p className={styles.summaryName}>{name}</p>
      </div>
    )}
    <div className={styles.summaryValueHolder}>
      <p className={styles.summaryLabel}>
        <div
          className={styles.summaryBullet}
          style={{
            backgroundColor: firstYearColor
          }}
        ></div>
        {firstYear} {label}
      </p>
      <p className={styles.summaryValue}>{firstYearValue}</p>
    </div>
    <div className={styles.summaryValueHolder}>
      <p className={styles.summaryLabel}>
        <div
          className={styles.summaryBullet}
          style={{
            backgroundColor: secondYearColor
          }}
        ></div>
        {secondYear} {label}
      </p>
      <p className={styles.summaryValue}>
        {secondYearValue}

        {isHighDeltaGood ? (
          <>
            {deltaPercentage > 0 ? (
              <span className={styles.deltaPercentageGood}>{deltaPercentage}%</span>
            ) : (
              <span className={styles.deltaPercentageBad}>{deltaPercentage}%</span>
            )}
          </>
        ) : (
          <>
            {deltaPercentage > 0 ? (
              <span className={styles.deltaPercentageBad}>{deltaPercentage}%</span>
            ) : (
              <span className={styles.deltaPercentageGood}>{deltaPercentage}%</span>
            )}
          </>
        )}
      </p>
    </div>
  </div>
)

const dataTypesToShow = {
  Consumed: 'Consumed',
  Cancellation: 'Cancellation',
  OTA: 'OTA',
  Direct: 'Direct'
}

const dataTypesToShowNames = {
  Consumed: 'Consumed',
  Cancellation: 'Cancelled',
  OTA: 'OTA',
  Direct: 'Direct'
}

function groupHotelsByCity(hotels) {
  return hotels.reduce((acc, hotel) => {
    const { city } = hotel

    if (acc[city]) {
      return { ...acc, [city]: [...acc[city], hotel] }
    } else {
      return { ...acc, [city]: [hotel] }
    }
  }, {})
}

const MonthlyRevenue = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [isErred, setIsErred] = useState(false)
  const [errorText, setErrorText] = useState('')

  const [response, setResponse] = useState()

  const [firstYear, setFirstYear] = useState()
  const [secondYear, setSecondYear] = useState()
  const [minimumAvailableYear, setMinimumAvailableYear] = useState()

  const [groupHotelData, setGroupHotelData] = useState()

  const [groupName, setGroupName] = useState()

  const [dataToShow, setDataToShow] = useState(dataTypesToShow.Consumed)

  const [hotelGroups, setHotelGroups] = useState()

  const [availableCities, setAvailableCities] = useState()

  const [groupToFilter, setGroupToFilter] = useState()

  const [stateToFilter, setStateToFilter] = useState('')
  const [cityToFilter, setCityToFilter] = useState('')

  const isInternalAdmin = useInternalAdmin()

  const [showAllHotels, setShowAllHotels] = useState(false)
  const [showWholeGroup, setShowWholeGroup] = useState(false)
  const [parentChain, setParentChain] = useState()

  const [cumulativeData, setCumulativeData] = useState()

  useEffect(() => {
    if (firstYear && !secondYear) {
      return
    }

    setIsFetching(true)

    let kind = 'CurrentSubsidiary'

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    performanceReportsApi
      .montlyBookings({
        firstYear,
        secondYear,
        kind,
        parentChainCode: parentChain,
        bookingFilterKind: dataToShow,
        city: cityToFilter,
        groupId: groupToFilter,
        stateCode: stateToFilter
      })
      .then(response => {
        setResponse(response)
        setFirstYear(response.firstYear.toString())
        setSecondYear(response.secondYear.toString())
        setMinimumAvailableYear(response.minimumAvailableYear)
        setGroupName(response.hotelName)
        setGroupHotelData(response.subsidiaries)
        setAvailableCities(response.cities)

        setCumulativeData(response.cumulative)

        setHotelGroups(
          response.hotelGroups.reduce((acc, hotelGroup) => {
            return { ...acc, [hotelGroup.id]: hotelGroup.name }
          }, {})
        )

        setIsErred(false)
      })
      .catch(err => {
        setIsErred(true)
        setCumulativeData()
        setGroupHotelData()

        if (err.toString().includes('Too many hotels to list')) {
          setErrorText('Too many hotels to list. Use more filters.')
        } else if (err.toString().includes('Location not found')) {
          setErrorText('No hotels found with these filters.')
        } else {
          setErrorText('Something went wrong')
        }
      })
      .finally(() => {
        setIsLoading(false)
        setIsFetching(false)
      })
  }, [
    firstYear,
    secondYear,
    showWholeGroup,
    parentChain,
    dataToShow,
    showAllHotels,
    cityToFilter,
    stateToFilter,
    groupToFilter
  ])

  function handleChangeState(state) {
    setStateToFilter(state)
    setCityToFilter('')
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  function handleChangeGroupFilter(value) {
    if (value === 'currentHotel') {
      setShowWholeGroup(false)
      setShowAllHotels(false)
      setGroupToFilter()
    }

    if (value === 'allHotels') {
      setShowWholeGroup(false)
      setShowAllHotels(true)
      setGroupToFilter()
    }

    if (value !== 'currentHotel' && value !== 'allHotels') {
      setGroupToFilter(value)
      setShowAllHotels(true)
      setShowWholeGroup(false)
    }
  }

  const groupedHotels = groupHotelData ? groupHotelsByCity(groupHotelData) : []

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Monthly Revenue" width={1080} />
      <div className={styles.topBar}>
        <RadioGroup
          label="Bookings to show"
          options={dataTypesToShowNames}
          value={dataToShow}
          onChange={setDataToShow}
        />
        {isInternalAdmin && (
          <Dropdown
            options={{
              currentHotel: '  Current hotel',
              allHotels: ' All hotels',
              ...hotelGroups
            }}
            defaultValue="Any group"
            label="Hotel group & level"
            onChange={e => handleChangeGroupFilter(e.target.value)}
          />
        )}
        {isInternalAdmin && (showAllHotels || showWholeGroup) && (
          <Dropdown
            options={{
              all: ' Any brand',
              ...parentChainNames
            }}
            value={parentChain}
            label="Brand"
            onChange={e => setParentChain(e.target.value)}
          />
        )}
        {isInternalAdmin && (showAllHotels || showWholeGroup) && (
          <Dropdown
            label="State/Province"
            options={{ '': ' Any state', ...usStates, ...canadianProvinces }}
            value={stateToFilter}
            onChange={e => handleChangeState(e.target.value)}
          />
        )}
        {isInternalAdmin && (showAllHotels || showWholeGroup) && (
          <Dropdown
            label="City"
            options={{
              '': ' Any city',
              ...availableCities
            }}
            value={cityToFilter}
            onChange={e => setCityToFilter(e.target.value)}
            defaultValue="Any city"
          />
        )}
        {firstYear && (
          <YearPicker
            firstYear={firstYear}
            secondYear={secondYear}
            minimumAvailableYear={minimumAvailableYear}
            setYears={(firstYear, secondYear) => {
              setFirstYear(firstYear)
              setSecondYear(secondYear)
            }}
          />
        )}
      </div>
      {isErred && (
        <div className={styles.noData}>
          <p>{errorText}</p>
        </div>
      )}
      {!isErred && (
        <>
          {cumulativeData && (
            <>
              <p className={styles.groupTitle}></p>
              <Charts
                data={cumulativeData}
                firstYear={response.firstYear}
                secondYear={response.secondYear}
                name={`Total data of ${groupHotelData?.length} hotels`}
                syncId={groupName}
                dataToShow={dataToShow}
              />
            </>
          )}
          {groupHotelData.length === 0 && !isFetching && (
            <div className={styles.noData}>
              <p>No matching hotels</p>
            </div>
          )}
          {groupHotelData.length === 0 && isFetching && (
            <div className={styles.noData}>
              <LoadingIndicator />
            </div>
          )}
          {groupHotelData.length > 0 && (
            <div className={styles.chartsHolder}>
              {groupHotelData.length > 0 &&
                Object.keys(groupedHotels)
                  .filter(s => !s.stateCode)
                  .sort((a, b) =>
                    groupedHotels[a][0].stateCode?.localeCompare(
                      groupedHotels[b][0]?.stateCode
                    )
                  )
                  .map(groupCategory => (
                    <div key={groupCategory}>
                      <p className={styles.groupTitle}>
                        {groupCategory}, {groupedHotels[groupCategory][0].stateCode}
                      </p>
                      <div className={styles.rowGroup}>
                        {groupedHotels[groupCategory].map(hotel => (
                          <Charts
                            firstYear={response.firstYear}
                            secondYear={response.secondYear}
                            data={hotel}
                            name={hotel.hotelName}
                            syncId={hotel.hotelName}
                            dataToShow={dataToShow}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const LaunchTooltip = props => {
  return (
    <g style={props.style}>
      <rect
        x={props.viewBox.x - 70}
        y={props.viewBox.y}
        fill="#2158CF"
        width={140}
        height={26}
        rx="15"
      />
      <text
        x={props.viewBox.x}
        y={props.viewBox.y - 6}
        dominant-baseline="middle"
        text-anchor="middle"
        fill="#FFFFFF"
        dy={20}
        dx={0}
        fontSize={11}
        fontWeight={500}
      >
        Launch - {moment(props.serviceStartDate).format('MMM DD, YYYY')}
      </text>
    </g>
  )
}

const MonthlyRevenueWithContext = () => (
  <AnalyticsContextWrapper>
    <MonthlyRevenue />
  </AnalyticsContextWrapper>
)

export default MonthlyRevenueWithContext
